import { addHours, endOfDay, getHours, getMinutes, isAfter, isSameDay, set } from "date-fns";

import { range } from "@app/util/arrayUtils";

// Returns TimePicker disabling props enforcing "time must be strictly
// after now" when `date` is today. When `date` is not today (or undefined),
// nothing is disabled.
export const getDisabledTimeForDate = (
  date: Date | undefined,
  // now is injectable for unit tests only; production callers typically omit it.
  now: Date = new Date()
): { disabledHours: () => number[]; disabledMinutes: (selectedHour: number) => number[] } => {
  if (!date || !isSameDay(date, now)) {
    return { disabledHours: () => [], disabledMinutes: () => [] };
  }
  const nowHour = getHours(now);
  const nowMinute = getMinutes(now);
  return {
    disabledHours: () => range(0, nowHour), // [0 .. nowHour - 1]
    disabledMinutes: (selectedHour: number) =>
      selectedHour === nowHour ? range(0, nowMinute + 1) : [], // [0 .. nowMinute] inclusive => strictly after now
  };
};

// If `date` is today and its time is at or before now, returns `date` with its
// time bumped forward to now + 1h (clamped to stay within today). Otherwise
// returns `date` unchanged.
// now is injectable for unit tests only; production callers typically omit it.
export const adjustStartAtIfPastNow = (date: Date, now: Date = new Date()): Date => {
  if (!isSameDay(date, now) || isAfter(date, now)) {
    return date;
  }
  const inOneHour = addHours(now, 1);
  const target = isSameDay(inOneHour, now)
    ? set(date, {
        hours: getHours(inOneHour),
        milliseconds: 0,
        minutes: getMinutes(inOneHour),
        seconds: 0,
      })
    : set(date, { hours: 23, milliseconds: 0, minutes: 59, seconds: 0 });
  return isAfter(target, now) ? target : date;
};

// Time window for auto-selecting a route's scheduled activities: from the later
// of the route's start and now — so already past-due activities are never
// auto-selected — through the end of the route's day.
// now is injectable for unit tests only; production callers typically omit it.
export const getScheduledActivitiesWindow = (
  routeStartAt: Date,
  now: Date = new Date()
): { from: Date; to: Date } => ({
  from: isAfter(routeStartAt, now) ? routeStartAt : now,
  to: endOfDay(routeStartAt),
});
